import { useEffect } from "react";

/**
 * 1NOW-1645 — per-tenant description, social-share and canonical tags.
 *
 * `useDocumentTitle` covers the tab title. Everything else in `<head>` was
 * still the static markup from `public/index.html`, so every tenant site
 * advertised itself as "Car rental" with no image: one description shared by
 * hundreds of customer-facing marketing sites, and every link pasted into
 * Slack, WhatsApp or a search result previewing identically.
 *
 * The tenant payload already carries what these tags need — `seo_title`,
 * `seo_description`, `social_share_image` — it simply was not being applied.
 *
 * ## What this does and does not fix
 *
 * Google and Bing execute JavaScript before indexing, so they see what this
 * writes. Most social scrapers (Facebook, Slack, WhatsApp, X) do **not** — they
 * read the raw HTML response and never run React. For those, the static
 * fallback in index.html is still what shows.
 *
 * Fixing that last case genuinely requires rendering the head server-side or
 * at the CDN edge, which is the architecture decision the ticket describes and
 * is deliberately not attempted here. This closes the crawler half of the
 * problem and leaves the scraper half explicit rather than silent.
 */

type MetaSource = {
  seo_title?: string | null;
  seo_description?: string | null;
  social_share_image?: string | null;
  company_logo?: string | null;
  [key: string]: any;
};

/**
 * Used when a tenant has not filled in their own SEO fields in the 1Now
 * dashboard — which is the common case for a freshly provisioned site.
 *
 * The per-account values from the API always win; these only fill the gap, so
 * a new site reads as a real rental business from day one instead of
 * advertising itself as "Car rental".
 *
 * There is no default share image on purpose: a generic stock card under
 * someone else's business name is worse than no card at all, so that stays
 * per-tenant and simply degrades to the plain summary card.
 *
 * KEEP IN SYNC with the fallback tags in `public/index.html` — that copy is
 * what non-JS social scrapers read, and the two should not disagree.
 */
export const DEFAULT_SEO_TITLE = "Car Rental — Book Smarter, Drive Sooner";
export const DEFAULT_SEO_DESCRIPTION =
  "Simple booking, transparent pricing, no counter queues.";

/** Create the tag on first use, then reuse it — never stack duplicates. */
const upsert = (
  selector: string,
  create: () => HTMLElement,
): HTMLElement => {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

const setMetaByName = (name: string, content?: string | null): void => {
  if (!content) return;
  const el = upsert(`meta[name="${name}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    return m;
  });
  el.setAttribute("content", content);
};

const setMetaByProperty = (property: string, content?: string | null): void => {
  if (!content) return;
  const el = upsert(`meta[property="${property}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("property", property);
    return m;
  });
  el.setAttribute("content", content);
};

const setCanonical = (href: string): void => {
  const el = upsert('link[rel="canonical"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "canonical");
    return l;
  });
  el.setAttribute("href", href);
};

export const useDocumentMeta = (details?: MetaSource | null): void => {
  // The account's own values from the API win; the shared defaults only fill
  // a gap the tenant has left empty.
  const title = details?.seo_title?.trim() || DEFAULT_SEO_TITLE;
  const description =
    details?.seo_description?.trim() || DEFAULT_SEO_DESCRIPTION;
  // A dedicated share image if the tenant set one, otherwise their logo —
  // a card with the operator's mark still beats a card with nothing.
  const image =
    details?.social_share_image?.trim() || details?.company_logo?.trim();

  // Wait for the payload before writing anything. Applying the defaults while
  // the request is still in flight would overwrite index.html's tags with the
  // same values and then immediately replace them, and on a tenant that does
  // have its own copy that is a visible flicker in the tab title.
  const loaded = Boolean(details);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!loaded) return;

    const url = window.location.origin + window.location.pathname;

    setMetaByName("description", description);
    setMetaByProperty("og:type", "website");
    setMetaByProperty("og:site_name", title);
    setMetaByProperty("og:title", title);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:url", url);
    setMetaByProperty("og:image", image);

    // summary_large_image only renders as a wide card if an image exists;
    // without one X falls back to the plain summary card, so gate on it.
    setMetaByName("twitter:card", image ? "summary_large_image" : "summary");
    setMetaByName("twitter:title", title);
    setMetaByName("twitter:description", description);
    setMetaByName("twitter:image", image);

    // Tenants are reachable on more than one host (custom domain plus the
    // *.1now.app fallback), which splits ranking between duplicates.
    setCanonical(url);
  }, [loaded, title, description, image]);
};

export default useDocumentMeta;
